'use client';

import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  initializeAppCheck, 
  ReCaptchaEnterpriseProvider, 
  AppCheck, 
  getToken
} from 'firebase/app-check';

// Classe para gerenciar Firebase e App Check
class FirebaseService {
  private static instance: FirebaseService;
  private app: FirebaseApp;
  private appCheck: AppCheck | null = null;
  private initialized = false;

  // Construtor privado para Singleton
  private constructor() {
    // Configuração Firebase
    const firebaseConfig = {
      apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
      authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
      storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID
    };

    // Inicializa Firebase
    this.app = initializeApp(firebaseConfig);
    this.initialized = false;
  }

  // Método para obter a instância (Singleton)
  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  // Inicializar App Check
  public initializeAppCheck(): void {
    // Verifica se está no browser e se já foi inicializado
    if (typeof window === 'undefined' || this.initialized) {
      return;
    }

    // Configurar token de depuração no ambiente de desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      // Usando o token gerado no console do Firebase
      // @ts-expect-error - FIREBASE_APPCHECK_DEBUG_TOKEN é uma propriedade não declarada no tipo Window
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.NEXT_PUBLIC_FIREBASE_APP_CHECK_DEBUG_TOKEN || true;
      console.log('App Check: Modo de depuração ativado');
    }

    try {
      // Verificar se a chave do reCAPTCHA está disponível
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        throw new Error('NEXT_PUBLIC_RECAPTCHA_SITE_KEY não configurada');
      }

      // Inicializar App Check
      this.appCheck = initializeAppCheck(this.app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true
      });

      this.initialized = true;
      console.log('App Check: Inicializado com sucesso');
    } catch (error) {
      console.error('App Check: Erro ao inicializar', error);
      throw error;
    }
  }

  // Obter token do App Check
  public async getAppCheckToken(forceRefresh = false): Promise<string> {
    if (!this.initialized || !this.appCheck) {
      this.initializeAppCheck();
    }

    if (!this.appCheck) {
      throw new Error('App Check não foi inicializado');
    }

    try {
      const tokenResult = await getToken(this.appCheck, forceRefresh);
      return tokenResult.token;
    } catch (error) {
      console.error('App Check: Erro ao obter token', error);
      
      // Para desenvolvimento, retornar um token fictício
      if (process.env.NODE_ENV === 'development') {
        console.warn('App Check: Retornando token fictício para ambiente de desenvolvimento');
        return 'development-mode-app-check-token-bypass';
      }
      
      throw error;
    }
  }

  // Getter para o app do Firebase
  public getApp(): FirebaseApp {
    return this.app;
  }

  // Verificar se App Check está inicializado
  public isAppCheckInitialized(): boolean {
    return this.initialized;
  }
}

// Exportar a instância singleton
export const firebaseService = FirebaseService.getInstance();

// Inicializar App Check automaticamente se estiver no browser
if (typeof window !== 'undefined') {
  firebaseService.initializeAppCheck();
}